import React from "react"
import { Button, Col, ControlLabel, Form, FormControl, FormGroup, Row, Table } from "react-bootstrap"
import { blockingFetchService } from "../../../lib/blockingFetch"
import {
  Currencies,
  DATE_FORMAT_STR,
  getDefaultCurrency,
  getOrganization,
  saveDefaultCurrency,
  validateDateState,
  validateTextState,
  VALIDATION_TYPE,
  saveOrganization,
  filterGLAccounts,
  sortGLAccounts
} from "../../../lib/utils"
import apiServerApi from "../../../redux/sagas/apiServerApi"
import FormControlInlineSelect from "../../fragments/FormControl.Inline.Select"
import FormControlInlineTextArea from "../../fragments/FormControl.Inline.TextArea"
import ProcessesStatus from "../../fragments/ProcessesStatus"
import { browserHistory } from "react-router"
import FontAwesome from "react-fontawesome"
import CustomBreadCrumb from "../../fragments/CustomBreadCrumb"
import moment from "moment"
import { DckActionCreators, DckSelectors } from "dck-redux"
import * as ItemTypes from "../../../redux/items/types"
import FormControlInlineDate from "../../fragments/FormControl.Inline.Date"
import { connect } from "react-redux"
import CurrencyRateForm from "./CurrencyRateForm"

const EntryType = {
  glAccountId: 'glAccountId',
  amount: 'amount'
}

class FormJournalEntry extends React.Component {

  constructor(props) {

    super(props)
    this.organization = getOrganization()
    this.extractBaseCurrencyCode(this.organization)

    this.state = {
      running: false,
      success: false,
      error: null,
      minDate: this.organization ? moment(this.organization.openedOn).format(DATE_FORMAT_STR) : '',
      transactionDate: moment().format(DATE_FORMAT_STR),
      currencyCode: getDefaultCurrency(),
      credits: [''],
      debits: [''],
      comments: '',
      rate: 1,
      currencyRates: [],

      transactionDateValidationState: VALIDATION_TYPE.SUCCESS,
      currencyCodeValidationState: VALIDATION_TYPE.SUCCESS,
      commentsValidationState: VALIDATION_TYPE.NULL,
    }
  }

  componentDidMount() {
    this.props.loadData()
    this.fetchLastClosingDate()
    this.fetchCurrencyRates()
    this.loadOrganization()
  }

  fetchLastClosingDate = () => blockingFetchService(
    accessToken => apiServerApi.getLastClosingDate(accessToken),
    minDate => {
      if (minDate) {
        this.setState({ minDate: moment(minDate).add(1, 'day').format(DATE_FORMAT_STR) })
      }
    },
    _ => {},
    _ => {}
  )

  fetchCurrencyRates = () => blockingFetchService(
    accessToken => apiServerApi.getCurrencyRates(accessToken),
    currencyRates => {
      this.setState({ currencyRates })
      this.updateRate(currencyRates, this.state.currencyCode)
    },
    _ => {},
    _ => {}
  )

  loadOrganization = () => {

    if(!this.organization) return

    blockingFetchService(
      accessToken => apiServerApi.getOrganizationById(accessToken, this.organization.id),
      organization => {
        this.extractBaseCurrencyCode(organization)
        saveOrganization(organization)
      },
      _ => {},
      _ => {}
    )
  }

  extractBaseCurrencyCode = organization => {
    this.baseCurrencyCode = organization && organization.defaultCurrencyCode ? 
      organization.defaultCurrencyCode : getDefaultCurrency()
  }

  updateRate = (currencyRates, from) => {

    const item = currencyRates.find(elt => elt.from === from && elt.to === this.baseCurrencyCode),
    rate = item ? item.rate : 1
    this.setState({ rate })
  }

  transactionDateChanged = transactionDate => this.setState({
    transactionDate,
    transactionDateValidationState: validateDateState(transactionDate, this.state.minDate),
    error: null,
  })

  currencyCodeChanged = currencyCode => {

    this.setState({
      currencyCode,
      currencyCodeValidationState: validateTextState(currencyCode, false),
      error: null,
    })

    const { currencyRates } = this.state
    this.updateRate(currencyRates, currencyCode)
  }

  commentsChanged = comments => this.setState({
    comments,
    commentsValidationState: validateTextState(comments, true),
    error: null,
  })

  debitsChanged = (type, value, index) => {

    const { debits } = this.state
    let current = debits[index]
    current = current ? current : {}
    current[type] = value
    debits[index] = current

    this.setState({
      debits,
      error: null,
    })
  }

  creditsChanged = (type, value, index) => {

    const { credits } = this.state
    let current = credits[index]
    current = current ? current : {}
    current[type] = value
    credits[index] = current

    this.setState({
      credits,
      error: null,
    })
  }

  validateAllValues = () => {

    const { transactionDate, currencyCode, comments, debits, credits } = this.state,
      validTrDate = validateDateState(transactionDate, this.state.minDate),
      validCurr = validateTextState(currencyCode, false),
      validCom = validateTextState(comments, true)

    this.setState({
      transactionDateValidationState: validTrDate,
      currencyCodeValidationState: validCurr,
      commentsValidationState: validCom,
    })

    try {
      if (validTrDate !== VALIDATION_TYPE.SUCCESS || validCurr !== VALIDATION_TYPE.SUCCESS
        || validCom === VALIDATION_TYPE.ERROR) {
        throw new Error('Some fields are incorrect.')
      }

      if (!debits.length || !credits.length) {
        throw new Error('Journal Entry must have at least one debit and one credit')
      }

      const debitsValid = debits.every(item => item && item.glAccountId && item.amount),
        creditsValid = credits.every(item => item && item.glAccountId && item.amount)
      if (!debitsValid || !creditsValid) {
        throw new Error('Please check debit and credit entries')
      }

      const sumDebits = debits.reduce((prev, current) => prev + Number(current.amount), 0),
        sumCredits = credits.reduce((prev, current) => prev + Number(current.amount), 0)
      if (sumDebits !== sumCredits) {
        throw new Error('Total credits should be equal to total debits')
      }

      return true
    }
    catch(e) {
      this.setState({ error: e.message })
      return false
    }
  }

  saveClicked = () => {
    if(this.validateAllValues()) {
      this.saveJournalEntry()
    }
  }

  applyRate = (data, rate) => data.map(({ glAccountId, amount }) => ({
    glAccountId,
    amount: Number(amount) * Number(rate)
  }))

  saveJournalEntry = () => {

    this.setState({
      running: true,
      success: false,
      error: null,
    })

    const { transactionDate, currencyCode, comments, debits, credits, rate } = this.state,
      data = {
        transactionDate,
        currencyCode: this.baseCurrencyCode,
        comments,
        debits: this.applyRate(debits, rate),
        credits: this.applyRate(credits, rate)
      }
    saveDefaultCurrency(currencyCode)

    blockingFetchService(
      accessToken => apiServerApi.createJournalEntry(accessToken, data),
      _ => {
        this.setState({ success: true })
        setTimeout(() => browserHistory.push('/journalentries'), 1000)
      },
      error => this.setState({ error: error.text }),
      _ => this.setState({ running: false })
    )
  }

  addNewDebit = () => {
    const { debits } = this.state
    debits.push('')
    this.setState({ debits })
  }

  addNewCredit = () => {
    const { credits } = this.state
    credits.push('')
    this.setState({ credits })
  }

  removeDebit = index => {
    const { debits } = this.state
    debits.splice(index, 1)
    this.setState({
      debits,
      error: null,
    })
  }

  removeCredit = index => {
    const { credits } = this.state
    credits.splice(index, 1)
    this.setState({
      credits,
      error: null,
    })
  }

  getItem = (name, index, glaccounts, glAccountId, amount, onChangeSelect, onChangeAmount, onRemove) =>

    <Table key={ `table${name}${index}` } striped condensed hover responsive className="mb-3">
      <tbody>
      <tr>
        <td className="w-40p">
          <FormControl componentClass="select" value={ glAccountId } onChange={ onChangeSelect }>

            <option value='' title={ `Select ${name}`}></option>
            { glaccounts.filter(filterGLAccounts).map(({fineractId, name}, index) =>
              <option key={ index } value={ fineractId } title={ name }>{ name }</option>)}
          </FormControl>
        </td>
        <td className="w-40p">
          <FormControl type="text" placeholder='Amount' value={ amount || '' } onChange={ onChangeAmount } />
        </td>
        <td>
          { index > 0 && 
            <span className="red cursor-pointer ml-20" onClick={ onRemove }>
              <FontAwesome name="remove" title='Remove' />
            </span>
          }
        </td>
      </tr>
      </tbody>
    </Table>

  getDebitItem = (item, index, glaccounts) => {

    const glAccountId = item ? item.glAccountId : '',
      amount = item ? item.amount : ''

    return this.getItem('debit', index, glaccounts, glAccountId, amount,
      e => this.debitsChanged(EntryType.glAccountId, e.target.value, index),
      e => this.debitsChanged(EntryType.amount, e.target.value, index),
      () => this.removeDebit(index)
    )
  }

  getCreditItem = (item, index, glaccounts) => {

    const glAccountId = item ? item.glAccountId : '',
      amount = item ? item.amount : ''

    return this.getItem('credit', index, glaccounts, glAccountId, amount,
      e => this.creditsChanged(EntryType.glAccountId, e.target.value, index),
      e => this.creditsChanged(EntryType.amount, e.target.value, index),
      () => this.removeCredit(index)
    )
  }

  renderDebits = (debits, glAccounts) => (
    <div>
      <h4>Debits</h4>
      { debits.map((item, index) => this.getDebitItem(item, index, glAccounts)) }
      <div>
        <Button onClick={ this.addNewDebit } bsStyle="info">
          <FontAwesome name="plus" /> New debit
        </Button>
      </div>
    </div>
  )

  renderCredits = (credits, glAccounts) => (
    <div className="mt-20">
      <h4>Credits</h4>
      { credits.map((item, index) => this.getCreditItem(item, index, glAccounts)) }
      <div>
        <Button onClick={ this.addNewCredit } bsStyle="info">
          <FontAwesome name="plus" /> New credit
        </Button>
      </div>
    </div>
  )

  renderDebitsCredits = () => {

    const { glAccounts } = this.props,

      { debits, credits } = this.state

    return (
      <FormGroup controlId='debitsCredits'>

        <Col componentClass={ ControlLabel } sm={4}>
          Affected GL entries
        </Col>

        <Col sm={8}>
          { this.renderDebits(debits, glAccounts) }
          { this.renderCredits(credits, glAccounts) }
        </Col>
      </FormGroup>
    )
  }

  renderCurrencyConverter = () => {

    const { currencyCode, rate } = this.state
    return <CurrencyRateForm
      choosenCurrencyCode={ currencyCode }
      baseCurrencyCode={ this.baseCurrencyCode }
      currentRate={ rate }
      currencyDataChanged={ this.fetchCurrencyRates }
    />
  }

  renderForm = () => {
    const { error, success, running, transactionDate, currencyCode, comments, minDate,
      transactionDateValidationState, commentsValidationState, currencyCodeValidationState } = this.state

    return (
      <div className="simple-form max-width-700 mx-auto mb-20">
        <Form horizontal onSubmit={e => { e.preventDefault(); this.saveClicked()}}>

          <h3 className="text-center">Add Journal Entry</h3>
          <hr />

          <FormControlInlineDate controlId="transactionDate" validationState={ transactionDateValidationState }
            label='Transaction date' placeholder='Choose a date' value={ transactionDate }
            onChange={ this.transactionDateChanged }
            helpText={ transactionDateValidationState === VALIDATION_TYPE.ERROR ? 'Choose a valid date' : '' }
            max={ moment().format(DATE_FORMAT_STR) }
            min={ minDate }
          />

          <FormControlInlineSelect
            controlId='currencyCode' validationState={ currencyCodeValidationState }
            label='Currency' value={ currencyCode } 
            onChange={ this.currencyCodeChanged }
            options={ Currencies }
            helpText={ currencyCode !== this.baseCurrencyCode ? 'A currency conversion will be applied' : '' }
          />

          { this.renderDebitsCredits() }

          <FormControlInlineTextArea
            controlId='comments' validationState={ commentsValidationState }
            label='Comments (Optional)' value={ comments }
            onChange={ this.commentsChanged }
          />
        </Form>

        <ProcessesStatus error={ error } running={ running } success={ success } />

        <div className="align-right mt-30">
          <Button onClick={() => browserHistory.push('/journalentries')} className="w-100" disabled={ running }>
            <FontAwesome name='close' /> Cancel
          </Button>

          <Button bsStyle="success" onClick={ this.saveClicked } className="w-120 ml-10"
            disabled={ running }>
            <FontAwesome name='save' /> Save
          </Button>
        </div>
      </div>
    )
  }

  render() {
    const dataBreadCrumbs = [
      { url: '/accounting', title: 'Accounting' },
      { url: '/journalentries', title: 'Journal entries' }
    ]

    return (
      <div>
        <CustomBreadCrumb activeText='New' data={ dataBreadCrumbs } />
        <Row>
          <Col mdOffset={1} md={6}>
            { this.renderForm() }
          </Col>
          <Col md={4}>
            { this.renderCurrencyConverter() }
          </Col>
        </Row>
      </div>
    )
  }
}

const mapStateToProps = state => {
  const glAccounts = DckSelectors.selectAllItems(state, ItemTypes.GLAccount)
  sortGLAccounts(glAccounts)
  return { glAccounts }
}

const mapDispatchToProps = dispatch => ({
  loadData: () => dispatch(DckActionCreators.itemsLoad(ItemTypes.GLAccount)),
})

export default connect(mapStateToProps, mapDispatchToProps)(FormJournalEntry)



// WEBPACK FOOTER //
// ./src/components/Accounting/JournalEntries/Form.js